import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Mic, Clock, BookAudio, Star } from "lucide-react";
import CatalogBrowser from "../components/CatalogBrowser";
import { useCatalog } from "../context/CatalogContext";
import { formatDuration } from "../mock";

/**
 * A narrator's shelf: the catalogue scoped to them, with the search, sort and
 * filter controls intact. In an audiobook a narrator sells as much as the
 * author, so this is a destination people arrive at and browse, not a dead-end
 * list.
 */
export default function NarratorPage() {
  const { name } = useParams();
  const narrator = decodeURIComponent(name || "");
  const { books } = useCatalog();

  const stats = useMemo(() => {
    const mine = books.filter((b) => b.narrator === narrator);
    const hours = Math.round(mine.reduce((s, b) => s + (b.duration || 0), 0) / 3600);
    const rated = mine.filter((b) => b.ratingCount > 0);
    const avg = rated.length
      ? rated.reduce((s, b) => s + b.rating, 0) / rated.length
      : 0;
    const authors = [...new Set(mine.map((b) => b.author))];
    return { count: mine.length, hours, avg, authors };
  }, [books, narrator]);

  const header = (
    <div className="mb-8">
      <div className="flex items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-sky-950 text-3xl font-bold shrink-0">
          {narrator.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-sky-400 font-semibold flex items-center gap-1.5">
            <Mic className="w-3.5 h-3.5" /> Narrator
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{narrator}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <BookAudio className="w-4 h-4" />
              {stats.count} {stats.count === 1 ? "audiobook" : "audiobooks"}
            </span>
            {stats.hours > 0 && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {formatDuration(stats.hours * 3600)} narrated
              </span>
            )}
            {stats.avg > 0 && (
              <span className="flex items-center gap-1.5 text-brand-400 font-medium">
                <Star className="w-4 h-4" fill="currentColor" /> {stats.avg.toFixed(1)} average
              </span>
            )}
          </div>
        </div>
      </div>

      {stats.authors.length > 0 && (
        <p className="mt-5 text-sm text-muted-foreground">
          Narrating work by{" "}
          {stats.authors.map((a, i) => (
            <React.Fragment key={a}>
              {i > 0 && (i === stats.authors.length - 1 ? " and " : ", ")}
              <Link
                to={`/author/${encodeURIComponent(a)}`}
                className="text-foreground hover:text-brand-400 transition-colors"
              >
                {a}
              </Link>
            </React.Fragment>
          ))}
          .
        </p>
      )}
    </div>
  );

  return <CatalogBrowser scope={{ narrator }} header={header} />;
}
